import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, ModelObjectWithDirtyCheck, type ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"

// Model Objects

export class MaintenanceRecord extends ModelObjectWithDirtyCheck<MaintenanceRecord> {

    public version: number = 0;
    public id: Guid = Guid.createEmpty();
    /**
    * Vessel which was maintained
    */
    public vesselId: Guid = Guid.createEmpty();
    /**
    * Timestamp this Maintenance was started
    */
    public startedAt: Instant = Instant.ofEpochMilli(0);
    private _endedAt: Instant | null = null;
    /**
    * Timestamp this Maintenance was completed
    */
    public get endedAt(): Instant | null { return this._endedAt }
    public set endedAt(value: Instant | null) {
      this._endedAt = value === undefined ? null : value;
    }
    /**
    * Use Number of the vessel when maintenance performed
    * Not Negative
    */
    public useNumber: number = 0;
    /**
    * Notes
    * Not Nullable
    * Max Length : 100000
    */
    public notes: string = "";
    private _assignedToUserId: Guid | null = null;
    /**
    * User, if any, currently assigned to perform this maintenance process.
    */
    public get assignedToUserId(): Guid | null { return this._assignedToUserId }
    public set assignedToUserId(value: Guid | null) {
      this._assignedToUserId = value === undefined ? null : value;
    }
    /**
    * Which Maintenance Recipe was performed
    */
    public maintenanceRecipeId: Guid = Guid.createEmpty();
    private _processId: Guid | null = null;
    /**
    * Process of which this record is associated to.
    */
    public get processId(): Guid | null { return this._processId }
    public set processId(value: Guid | null) {
      this._processId = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.version = jsonSerializedObject.version;
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.vesselId = Guid.parseOrEmpty(jsonSerializedObject.vesselId);
          this.startedAt = Instant.parse(jsonSerializedObject.startedAt);
          this.endedAt = jsonSerializedObject.endedAt == null ? null : Instant.parse(jsonSerializedObject.endedAt);
          this.useNumber = jsonSerializedObject.useNumber;
          this.notes = jsonSerializedObject.notes;
          this.assignedToUserId = jsonSerializedObject.assignedToUserId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.assignedToUserId);
          this.maintenanceRecipeId = Guid.parseOrEmpty(jsonSerializedObject.maintenanceRecipeId);
          this.processId = jsonSerializedObject.processId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.processId);
        }

        this.setDirtyCheckpoint();
    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.version = this.version;
        toRet.id = this.id.toString();
        toRet.vesselId = this.vesselId.toString();
        toRet.startedAt = this.startedAt.toString();
        toRet.endedAt = this.endedAt == null ? null : this.endedAt.toString();
        toRet.useNumber = this.useNumber;
        toRet.notes = this.notes;
        toRet.assignedToUserId = this.assignedToUserId == null ? null : this.assignedToUserId.toString();
        toRet.maintenanceRecipeId = this.maintenanceRecipeId.toString();
        toRet.processId = this.processId == null ? null : this.processId.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "version", rule: (v: any) => v == null || v >= -9223372036854775808 || "Version is not greater than minimum allowed value: -9223372036854775808"},
            {field: "version", rule: (v: any) => v == null || v <= 9223372036854775807 || "Version is above maximum allowed value: 9223372036854775807"},
            {field: "id", rule: (v: any) => !v.isEmpty() || "MaintenanceRecord.Id cannot be empty"},
            {field: "vesselId", rule: (v: any) => !v.isEmpty() || "MaintenanceRecord.VesselId cannot be empty"},
            {field: "useNumber", rule: (v: any) => v == null || v >= 0 || "UseNumber is not greater than minimum allowed value: 0"},
            {field: "useNumber", rule: (v: any) => v == null || v <= 2147483647 || "UseNumber is above maximum allowed value: 2147483647"},
            {field: "notes", rule: (v: any) => v == null || v.length <= 100000 || "Notes has too many characters (100000)"},
            {field: "assignedToUserId", rule: (v: any) => v == null || !v.isEmpty() || "MaintenanceRecord.AssignedToUserId cannot be empty"},
            {field: "maintenanceRecipeId", rule: (v: any) => !v.isEmpty() || "MaintenanceRecord.MaintenanceRecipeId cannot be empty"},
            {field: "processId", rule: (v: any) => v == null || !v.isEmpty() || "MaintenanceRecord.ProcessId cannot be empty"},
    ];
}


export class VesselMaintenanceRecordService extends ServiceBase {

  public async getMaintenanceRecordsForVessel(vesselId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(MaintenanceRecord)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    vesselId:vesselId.toString(),
    };
    const extractor = (response:any) => response.records.map((v: any) => new MaintenanceRecord(v));;
    return this.makeJsonRequest("vesselMaintenanceRecord/v1/getMaintenanceRecordsForVessel", req, false, extractor, opts);
  };

  public async getIncompleteMaintenanceRecordsForVessels(vesselIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(MaintenanceRecord)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    vesselIds:vesselIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.records.map((v: any) => new MaintenanceRecord(v));;
    return this.makeJsonRequest("vesselMaintenanceRecord/v1/getIncompleteMaintenanceRecordsForVessels", req, false, extractor, opts);
  };

  public async saveMaintenanceRecord(record: MaintenanceRecord, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    record:record.toJsonSerializedObject(),
    };
    const extractor = null;
    return this.makeJsonRequest("vesselMaintenanceRecord/v1/saveMaintenanceRecord", req, true, extractor, opts);
  };

  public async deleteMaintenanceRecord(recordId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    recordId:recordId.toString(),
    };
    const extractor = null;
    return this.makeJsonRequest("vesselMaintenanceRecord/v1/deleteMaintenanceRecord", req, true, extractor, opts);
  };
}

export const vesselMaintenanceRecordService = new VesselMaintenanceRecordService();